import type { RefOrGetter } from "ls/common/types";
import { useQuery } from "@tanstack/vue-query";
import { computed, toValue } from "vue";
import { useSharedAssetWorkspaces } from "../templates/useSharedAssetWorkspace";
import { usePrivateWorkspace } from "../workspaces/usePrivateWorkspace";
import { isSharedAssetType } from "../workspaces/utils";
import { queries } from "./queries";
import { ID_SEP, useFuzzySearch } from "./useFuzzySearch";

export interface UseSearchOptions {
  limit?: number;
  excludeViewOnly?: RefOrGetter<boolean>;
  excludeArchived?: RefOrGetter<boolean>;
  excludePrivate?: RefOrGetter<boolean>;
  excludeSharedAssets?: RefOrGetter<boolean>;
  onlyForOrganizationId?: RefOrGetter<string | undefined>;
  onlyForWorkspaceId?: RefOrGetter<string | undefined>;
}

export function useLocalSearch(searchTerm: RefOrGetter<string>, options: UseSearchOptions = {}) {
  const { data, isPending, isLoading, isFetching, isSuccess } = useQuery(queries.userSearchData());
  const privateWs = usePrivateWorkspace();

  const {
    limit,
    excludeViewOnly,
    excludeArchived,
    excludePrivate,
    excludeSharedAssets,
    onlyForOrganizationId,
    onlyForWorkspaceId,
  } = options;

  const { workspaces: sharedAssets } = useSharedAssetWorkspaces(() => toValue(onlyForOrganizationId));

  const allWorkspaces = computed(() => data.value?.Filesystems ?? []);
  const allFolders = computed(() => data.value?.Folders ?? []);
  const allFiles = computed(() => data.value?.Shortcuts ?? []);

  const workspacesMap = computed(() =>
    new Map(allWorkspaces.value.map(w => [w.Filesystem.FilesystemId, w] as const)),
  );
  const shortcutsMap = computed(() =>
    new Map(allFiles.value.map(s => [s.Shortcut.Shortcut, s] as const)),
  );

  const foldersMap = computed(() =>
    new Map(allFolders.value.map(f => [f.Folder.FolderId, f] as const)),
  );

  const filteredWorkspaces = computed(() => {
    const orgId = toValue(onlyForOrganizationId);
    const wsId = toValue(onlyForWorkspaceId);
    return allWorkspaces.value
      .filter(f => !(wsId && f.Filesystem.FilesystemId !== wsId))
      .filter(f => !(orgId && f.Filesystem.SubInstanceId !== orgId))
      .filter(f => !(toValue(excludeSharedAssets) && isSharedAssetType(f.Filesystem.FilesystemType)))
      .filter(f => !(toValue(excludePrivate) && f.Filesystem.IsDefaultFs))
      .filter(f => !(toValue(excludeViewOnly) && f.Filesystem.ViewerFs))
      .filter(f => !(toValue(excludeArchived) && f.Filesystem.IsArchived));
  });

  const filteredFolders = computed(() => {
    const orgId = toValue(onlyForOrganizationId);
    const wsId = toValue(onlyForWorkspaceId);
    return allFolders.value
      .filter(f => !(wsId && f.FilesystemId !== wsId))
      .filter(f => !(orgId && f.SubInstanceId !== orgId))
      .filter(f => !(toValue(excludeSharedAssets) && sharedAssets.value?.some(sa => sa.FilesystemId === f.FilesystemId)))
      .filter(f => !(toValue(excludePrivate) && f.FilesystemId === privateWs.value?.FilesystemId))
      .filter(f => !(toValue(excludeViewOnly) && f.IsViewer))
      .filter(f => !(toValue(excludeArchived) && f.IsArchived));
  });

  const filteredFiles = computed(() => {
    const orgId = toValue(onlyForOrganizationId);
    const wsId = toValue(onlyForWorkspaceId);
    return allFiles.value
      .filter(f => !(wsId && f.Shortcut.FilesystemId !== wsId))
      .filter(f => !(orgId && f.Shortcut.SubInstanceId !== orgId))
      .filter(f => !(toValue(excludeSharedAssets) && sharedAssets.value?.some(sa => sa.FilesystemId === f.Shortcut.FilesystemId)))
      .filter(f => !(toValue(excludePrivate) && f.Shortcut.FilesystemId === privateWs.value?.FilesystemId))
      .filter(f => !(toValue(excludeViewOnly) && f.Shortcut.InFsViewerFolder))
      .filter(f => !(toValue(excludeArchived) && f.IsArchived));
  });

  const workspaces = useFuzzySearch(filteredWorkspaces, f => f.Filesystem.DisplayName, searchTerm, { limit });
  const folders = useFuzzySearch(filteredFolders, f => f.Folder.FolderName, searchTerm, { limit });
  const files = useFuzzySearch(filteredFiles, f => `${f.Shortcut.Name}${ID_SEP}${f.Shortcut.Shortcut}`, searchTerm, { limit, hideIdHighlight: true });

  return {
    workspaces,
    folders,
    files,
    workspacesMap,
    foldersMap,
    shortcutsMap,
    isPending,
    isLoading,
    isFetching,
    isSuccess,
  };
}
